import { Container } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import {
  DEFAULT_END_INTERVAL,
  DEFAULT_START_INTERVAL,
  ensureLocalStartOfMonth,
  getNextMonth,
} from '@resistapp/common/friendly';
import { flattenSamplesByUID } from '@resistapp/common/utils';
import { subHours } from 'date-fns';
import { chain, isDate } from 'lodash';
import { useCallback, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { theme } from '../../shared/theme'; // Ensure theme is imported

export function TimeFilterSelector() {
  const { queryFilters, data } = useSampleDataContext();

  const allSamplesInArray = flattenSamplesByUID(data?.samplesByUID || {});
  const uniqueDates = chain(allSamplesInArray)
    .map('time')
    .filter(isDate)
    .uniqBy(date => date.getTime())
    .sortBy(date => date.getTime())
    .value();
  const firstDate = uniqueDates.length > 0 ? uniqueDates[0] : null;
  const lastDate = uniqueDates.length > 0 ? uniqueDates[uniqueDates.length - 1] : null;
  const hasMultipleYears = firstDate && lastDate && firstDate.getFullYear() !== lastDate.getFullYear();

  // When the date is in year 1900, it means that the month is not selected
  const selectedStartMonth =
    queryFilters.filters.interval.start.getFullYear() === DEFAULT_START_INTERVAL.getFullYear()
      ? ensureLocalStartOfMonth(firstDate || DEFAULT_START_INTERVAL)
      : ensureLocalStartOfMonth(queryFilters.filters.interval.start);
  const selectedEndMonth =
    queryFilters.filters.interval.end.getFullYear() === DEFAULT_END_INTERVAL.getFullYear()
      ? ensureLocalStartOfMonth(lastDate || DEFAULT_END_INTERVAL)
      : ensureLocalStartOfMonth(getPreviousDay(ensureLocalStartOfMonth(queryFilters.filters.interval.end)));
  const { setMonthStable } = queryFilters;

  // These control the date picker and show which dates are being selected or have been selected
  const [selectingStartDate, setSelectingStartDate] = useState<Date | null>(selectedStartMonth);
  const [selectingEndDate, setSelectingEndDate] = useState<Date | null>(selectedEndMonth);
  const [isOpen, setIsOpen] = useState(false);
  const datePickerRef = useRef<DatePicker>(null);

  const onChange = useCallback(
    (dates: [Date | null, Date | null]) => {
      const [start, end] = Array.isArray(dates) ? dates : [dates, null];

      // This doesn't happen currently, but if the user would like to clear all selections
      if (!start) {
        setMonthStable(null, null);
        return;
      } else if (!end) {
        // User selected the first part (start of the month range). Set end to null for datepicker
        setSelectingStartDate(start);
        setSelectingEndDate(null);
        return;
      }

      // User selected a range of months. Set both start and end
      setMonthStable(start, getNextMonth(end));
      setSelectingStartDate(start);
      setSelectingEndDate(end);
      // Close the date picker when a complete range is selected
      setIsOpen(false);
      blurActiveElement();
    },
    [setMonthStable],
  );

  const handleClick = useCallback(() => {
    if (isOpen) {
      // We are about to close – blur the input so it does not stay active
      blurActiveElement();
    }
    setIsOpen(prev => !prev);
  }, [isOpen]);

  return (
    <Container pl="0px" pr="0px" width="100%" maxWidth="none">
      <StyledDatePickerWrapper isOpen={isOpen}>
        <DatePicker
          ref={datePickerRef}
          showMonthYearPicker
          selectsRange
          showIcon
          dateFormat="MMM yyyy"
          includeDates={uniqueDates}
          selected={selectingStartDate}
          startDate={selectingStartDate}
          endDate={selectingEndDate}
          monthsShown={hasMultipleYears ? 2 : 1}
          onChange={onChange}
          open={isOpen}
          onClickOutside={() => {
            setIsOpen(false);
            blurActiveElement();
          }}
          onInputClick={handleClick}
          popperClassName={isOpen ? 'active-datepicker-popper' : ''}
        />
      </StyledDatePickerWrapper>
    </Container>
  );
}

function getPreviousDay(date: Date) {
  return subHours(date, 12);
}

// Helper to blur the currently active element (e.g. the date input)
function blurActiveElement() {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
}

// Define color constants from the theme to potentially help with linter type inference
const activeBorderColor = theme.colors.blue500;
const inactiveBorderColor = theme.colors.neutral300;
const hoverBorderColor = theme.colors.neutral400;
const activeBoxShadow = `0 0 0 1px ${theme.colors.blue500}`;
const activePopoverShadow = `0 0 0 0px ${theme.colors.blue500}`;

// Styled wrapper to make react-datepicker full width and apply active styles
const StyledDatePickerWrapper = styled.div<{ isOpen?: boolean }>`
  width: 100%;

  .react-datepicker-wrapper,
  .react-datepicker__input-container {
    width: 100%;
  }

  .react-datepicker__input-container input {
    width: 100%;
    border: 1px solid ${inactiveBorderColor};
    border-radius: 0.375rem; // Chakra md border-radius
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;
    height: 40px; // Consistent height, matches Chakra 'md' size
    padding-left: 2.5rem; // Increased padding-left to accommodate the icon from showIcon
    padding-right: 0.75rem;
    box-sizing: border-box; // Ensure padding and border are included in the height

    ${props =>
      props.isOpen &&
      `
      border-color: ${activeBorderColor};
      box-shadow: ${activeBoxShadow};
    `}

    &:hover {
      border-color: ${props => (props.isOpen ? activeBorderColor : hoverBorderColor)};
    }
  }

  .active-datepicker-popper .react-datepicker {
    border: 1px solid ${activeBorderColor};
    box-shadow: ${activePopoverShadow};
    border-radius: 0.3rem; // react-datepicker default
    margin-top: 2px; // Optional: to prevent visual overlap of shadows
  }

  .react-datepicker__triangle {
    display: none;
  }
`;
